import {useMemo, useState} from 'react'
import {useMutation, useQuery} from '@apollo/client'
import {Breadcrumb, Button, Col, Form, InputGroup, Row, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {GET_ALL_POLICIES_STATUSES} from '../../gql/queries/policiesQueries'
import {DELETE_POLICY_STATUS} from '../../gql/mutations/policyStatusMutations'
import {useDisclourse} from '../../hooks/useDisclourse'
import {getBootstrapColorHex} from '../../helpers/consts'
import {useGetUser} from '../../store/userStore'
import {ModalAddEditPolicyStatus} from './components/ModalAddEditPolicyStatus'
import {IResDeletePolicyStatus, IResPolicyStatuses, PolicyStatusNode} from './policyStatusTypes'

const getFirstError = (errors?: {message?: string}[]) => errors?.[0]?.message || 'Something happened'

export const PolicyStatusPage = () => {
  const userGroups = useGetUser((s) => s.user?.groups)?.map((group: any) => group.name || group) || []
  const canManageReservedStatuses =
    userGroups.includes('Administrador') || userGroups.includes('SuperAdministrador')
  const {data, loading, error, refetch} = useQuery<IResPolicyStatuses>(GET_ALL_POLICIES_STATUSES, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
    onError: (err) => toast.error(`Error: ${err.message}`),
  })
  const [deletePolicyStatus, {loading: deleting}] =
    useMutation<IResDeletePolicyStatus>(DELETE_POLICY_STATUS)
  const {isOpen, onClose, onOpen} = useDisclourse()
  const [selectedStatus, setSelectedStatus] = useState<PolicyStatusNode | undefined>()
  const [text, setText] = useState('')

  const policyStatuses = useMemo(() => {
    const normalizedText = text.trim().toLowerCase()
    const rows = data?.allPolicyStatuses?.edges.map(({node}) => node) || []
    if (!normalizedText) return rows

    return rows.filter((status) =>
      [status.code, status.title, status.category]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedText))
    )
  }, [data, text])

  const handleDelete = async (status: PolicyStatusNode) => {
    if (status.systemReserved && !canManageReservedStatuses) {
      toast.error('Only Administrador or SuperAdministrador can manage reserved policy statuses.')
      return
    }

    if (!window.confirm(`Delete policy status "${status.title}"?`)) return

    const res = await deletePolicyStatus({variables: {code: status.code}})
    if (res.data?.deletePolicyStatus?.success) {
      toast.success('Policy status deleted correctly')
      refetch()
      return
    }

    toast.error(`Error: ${getFirstError(res.data?.deletePolicyStatus?.errors)}`)
  }

  if (loading && !data) {
    return (
      <div>
        <Spinner animation='border' className='ms-4' />
      </div>
    )
  }

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='d-blok d-md-none'>
            <span>
              <i className='bi bi-list fs-1' />
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Policy Statuses</Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex align-items-center gap-2'>
          <Button
            size='sm'
            onClick={() => {
              setSelectedStatus(undefined)
              onOpen()
            }}
          >
            <i className='bi bi-plus fs-3' /> New Status
          </Button>
        </div>
      </header>
      <Row className='policies'>
        <Col md={12} lg={12} sm={12} xl={12} className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>All Policy Statuses</h3>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder='Search by code, title, or category...'
                  onChange={(e) => setText(e.target.value)}
                  value={text}
                />
                <Button
                  onClick={() => refetch()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  Search
                </Button>
              </InputGroup>
            </div>
            <div className='results-header'>
              <div className='px-2 py-2 fst-italic'>
                Showing {policyStatuses.length} of {data?.allPolicyStatuses?.edges.length || 0} entries
              </div>
            </div>
            <div className='list-carrier table-scrollable table-container'>
              <table className='table-scrollable table-scroll table table-striped table-hover'>
                <thead>
                  <tr role='row'>
                    <th className='fw-bold'>Code</th>
                    <th className='fw-bold'>Title</th>
                    <th className='fw-bold d-none d-lg-table-cell'>Category</th>
                    <th className='fw-bold text-center'>Color</th>
                    <th className='fw-bold text-center'>Agent Access</th>
                    <th className='fw-bold text-center d-none d-md-table-cell'>System</th>
                    <th className='fw-bold text-center'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {policyStatuses.map((status) => {
                    const previewColor = getBootstrapColorHex(status.color)
                    const reservedLocked = status.systemReserved && !canManageReservedStatuses
                    return (
                      <tr key={status.code}>
                        <td className='px-4 fw-semibold'>{status.code}</td>
                        <td className='px-2'>{status.title}</td>
                        <td className='px-2 d-none d-lg-table-cell'>{status.category}</td>
                        <td className='px-2 text-center'>
                          <span
                            className='badge'
                            style={{backgroundColor: previewColor, color: '#fff'}}
                          >
                            {status.color}
                          </span>
                        </td>
                        <td className='px-2 text-center'>
                          <span className={`badge ${status.enabledForAgents ? 'bg-success' : 'bg-secondary'}`}>
                            {status.enabledForAgents ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className='px-2 text-center d-none d-md-table-cell'>
                          <span className={`badge ${status.systemReserved ? 'bg-primary text-white' : 'bg-light text-dark'}`}>
                            {status.systemReserved ? 'Reserved' : 'Custom'}
                          </span>
                        </td>
                        <td className='px-2 text-center'>
                          <div className='d-flex align-items-center justify-content-center gap-3'>
                            <button
                              title={reservedLocked ? 'Reserved statuses can only be edited by Administrador or SuperAdministrador' : 'Edit Policy Status'}
                              style={{border: 0, backgroundColor: 'transparent', padding: 0, margin: 0}}
                              disabled={reservedLocked}
                              onClick={() => {
                                if (reservedLocked) return
                                setSelectedStatus(status)
                                onOpen()
                              }}
                            >
                              <i className={`bi bi-pencil-fill ${reservedLocked ? 'text-muted' : 'text-primary'}`} />
                            </button>
                            <button
                              title={reservedLocked ? 'Reserved statuses can only be deleted by Administrador or SuperAdministrador' : 'Delete Policy Status'}
                              style={{border: 0, backgroundColor: 'transparent', padding: 0, margin: 0}}
                              disabled={deleting || reservedLocked}
                              onClick={() => handleDelete(status)}
                            >
                              <i className={`bi bi-trash-fill ${reservedLocked ? 'text-muted' : 'text-danger'}`} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                  {policyStatuses.length === 0 && !error && (
                    <tr>
                      <td colSpan={7} className='text-center text-muted py-5'>
                        No policy statuses found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && (
        <ModalAddEditPolicyStatus
          isOpen={isOpen}
          onClose={onClose}
          info={selectedStatus}
          refetch={refetch}
          canManageReserved={canManageReservedStatuses}
        />
      )}
    </div>
  )
}
